import * as React from 'react'
import {ReactElement, ReactNode} from 'react'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {BlockSpacer, currentMedia, GridContainer, GridItem} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop, DisplayItemLabelDiv} from '../../../../styledComponents/DisplayItem'
import {StyledSecondaryButton} from '../../../../styledComponents/Button'
import {Box} from '@mui/material'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {Nullable} from '../../../../app/TypeHelpers'
import {RESIDENT_APPLY_STATUS} from '../../../../CodeMaster'
import {NatResidentApplicationHistoryInfoComponent} from '../common/resident/NatResidentApplicationHistoryInfoComponent'
import styled from 'styled-components'
import {FullWidthColumnFlexContainer, RowFlexContainer} from '../../../../styledComponents/FlexComponents'
import {NatGetApplicationResultDetail} from '../repository/NatGetApplicationResult'
import {HKConstants} from '../../../../HKConstants'
import AuthorityChecker from '../../../../components/AuthorityChecker'
import NatResidentBrowseConfirm from './resident/NatResidentBrowseConfirm'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {convertApplyStatusIntoApplyHistoryDivision} from '../common/helper/NatApplicationHelper'
import {NatBrowseApplyDetail} from '../repository/NatGetBrowseApply'
import {NatBrowseApplyAccompany, NatBrowseApplyTime} from './NatResidentApplicationInterface'
import {Colors, FontSize} from '../../../../styledComponents/Constants'
import {convertToMoment} from '../../../../components/reservations/helpers/helpers'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId: string
    applicationRepo: NatApplicationRepo
    location: Location
}

interface States {
    browseApplyDetail: NatBrowseApplyDetail
    applicationResultDetail: NatGetApplicationResultDetail
    isLoadCompleted: boolean
}

class NatResidentBrowsePage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, States> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            browseApplyDetail: {
                applyId: '',
                applyStatus: '',
                condominiumName: '',
                ridgeName: '',
                roomNumber: '',
                applicantFamilyName: '',
                applicantFirstName: '',
                applyDate: '',
                browseReasonDetail: '',
                videoRecordingPlace: '',
                videoRecordingDate: '',
                videoRecordingStartTime: '',
                videoRecordingEndTime: '',
                browsePreferredDate: '',
                browsePreferredStartTime: '',
                browsePreferredEndTime: '',
                browseDate: null,
                browseStartTime: null,
                browseEndTime: null,
                witness1RidgeNameRoomNumber: null,
                witness1Name: null,
                witness2RidgeNameRoomNumber: null,
                witness2Name: null,
                otherWitness: null,
                accompany: null,
                note: null,
                version: 0,
            },
            applicationResultDetail: {
                applyInfo: {
                    applyId: '',
                    applyCategory: '',
                    ridgeName: '',
                    roomNumber: '',
                    familyName: '',
                    firstName: '',
                    registerDatetime: '',
                },
                resultInfo: {
                    applyStatus: '',
                    roleId: '',
                    comment: '',
                    registerDatetime: '',
                },
            },
            isLoadCompleted: false,
        }

        this.onClickReApplyButton = this.onClickReApplyButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getApplyInfo()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({
                    isLoadCompleted: true,
                },
            )
        }
    }

    async getApplyInfo(): Promise<void> {
        const [browseApplyResponse, applicationStatusResponse] = await Promise.all([
            this.getBrowseApply(),
            this.getApplicationResult(),
        ])

        if (browseApplyResponse && applicationStatusResponse) {
            this.setState({
                browseApplyDetail: browseApplyResponse,
                applicationResultDetail: applicationStatusResponse,
            })
        }
    }

    async getBrowseApply(): Promise<Nullable<NatBrowseApplyDetail>> {
        const {condominiumCd, applyId, applicationRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            applicationRepo.getBrowseApplyForResident.bind(applicationRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    async getApplicationResult(): Promise<Nullable<NatGetApplicationResultDetail>> {
        const {condominiumCd, applyId, applicationRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            applicationRepo.getApplyResult.bind(applicationRepo, condominiumCd, applyId),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    onClickReApplyButton(): void {
        this.props.navigate(
            `/resident/condominiums/${this.props.condominiumCd}/applications/browses/${this.props.applyId}/copy`,
        )
    }

    convertBrowseApplyTime(timeString: string): NatBrowseApplyTime {
        return {
            time: timeString,
            hours: '',
            minutes: '',
        }
    }

    addCardDetail(): Nullable<ReactNode> {
        const {
            browseDate, browseStartTime, browseEndTime, witness1RidgeNameRoomNumber,
            witness1Name, witness2RidgeNameRoomNumber, witness2Name, otherWitness,
        } = this.state.browseApplyDetail

        if (!(browseDate && browseStartTime && browseEndTime)) {
            return null
        }

        const formatBrowseDate = convertToMoment(browseDate, null).format('YYYY年M月D日')
        const formatBrowseStartTime = convertToMoment(browseDate, browseStartTime).format('H:mm')
        const formatBrowseEndTime = convertToMoment(browseDate, browseEndTime).format('H:mm')
        const browseDateTime: string = `${formatBrowseDate} ${formatBrowseStartTime}～${formatBrowseEndTime}`

        let witnesses: string[] = []
        witnesses.push([witness1RidgeNameRoomNumber, witness1Name].filter(Boolean).join('　'))
        witnesses.push([witness2RidgeNameRoomNumber, witness2Name].filter(Boolean).join('　'))
        witnesses.push([otherWitness].filter(Boolean).join('　'))
        witnesses = witnesses.filter(Boolean)

        return (
            <Box>
                <CardContainerStyle>閲覧日時</CardContainerStyle>
                <Box fontSize={FontSize.Middle}>
                    {browseDateTime}
                </Box>
                <Box height={'10px'}/>
                {witnesses.length > 0 &&
                    <>
                        <CardContainerStyle>立会いする理事等</CardContainerStyle>
                        <Box fontSize={FontSize.Middle} whiteSpace={'pre-wrap'}>
                            {witnesses.join('\n')}
                        </Box>
                        <Box height={'10px'}/>
                    </>
                }
            </Box>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        const convertAccompany: Nullable<NatBrowseApplyAccompany[]> =
            this.state.browseApplyDetail.accompany ?
                this.state.browseApplyDetail.accompany.map((list) => ({
                    index: list.seq,
                    ...list,
                })) : null

        return (
            <>
                <PlainBackLink to={`/resident/condominiums/${this.props.condominiumCd}/applications/history`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    申請内容
                </PageTitle>
                <Box height={'20px'}/>
                <NatResidentApplicationHistoryInfoComponent
                    applyId={this.props.applyId}
                    applyHistoryDivision={
                        convertApplyStatusIntoApplyHistoryDivision(this.state.browseApplyDetail.applyStatus)}
                    applyResultInfo={this.state.applicationResultDetail}
                    cardDetail={this.addCardDetail()}
                />
                <NatResidentBrowseConfirm
                    {...this.state.browseApplyDetail}
                    videoRecordingStartTime=
                        {this.convertBrowseApplyTime(this.state.browseApplyDetail.videoRecordingStartTime)}
                    videoRecordingEndTime=
                        {this.convertBrowseApplyTime(this.state.browseApplyDetail.videoRecordingEndTime)}
                    browsePreferredStartTime=
                        {this.convertBrowseApplyTime(this.state.browseApplyDetail.browsePreferredStartTime)}
                    browsePreferredEndTime=
                        {this.convertBrowseApplyTime(this.state.browseApplyDetail.browsePreferredEndTime)}
                    accompany={convertAccompany}
                    applyFlag={false}
                />
                <BlockSpacer height={'5px'}/>
                <ItemDiv>
                    <GridContainer alignItems={'flex-start'}>
                        <GridItem over={3}>
                            <DisplayItemLabelDiv>
                                <Box mr={'5px'}>
                                    許可条件
                                </Box>
                            </DisplayItemLabelDiv>
                        </GridItem>
                        <GridItem over={9}>
                            <RowFlexContainer>
                                <Box whiteSpace={'pre-wrap'}>
                                    一 録画映像を閲覧した者（立会いした理事及び第三者を含む）は、映像内容及び関連情報について守秘義務を負うものとする。<br/>
                                    二 録画映像を閲覧した者（立会いした理事及び第三者を含む）は、
                                    閲覧の結果得た情報を、犯罪行為・迷惑行為の防止・摘発、共用部分等の汚損・毀損行為等の防止・
                                    摘発並びに共用部分等の損傷に対する損害賠償の請求、管理組合及び居住者に損害を与える可能性がある事象の検証等のためにのみ使用できるものとする。
                                </Box>
                            </RowFlexContainer>
                        </GridItem>
                    </GridContainer>
                </ItemDiv>
                <AuthorityChecker permittedAuthorities={HKConstants.CMN.AUTH.APPLY_BROWSE_APPLICATION}>
                    {(this.state.browseApplyDetail.applyStatus === RESIDENT_APPLY_STATUS.COMPLETE.value ||
                            this.state.browseApplyDetail.applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) &&
                        <ReApplyButton
                            onClick={this.onClickReApplyButton}>
                            <FullWidthColumnFlexContainer>
                                <div>
                                    内容を編集して再申請する
                                </div>
                            </FullWidthColumnFlexContainer>
                        </ReApplyButton>
                    }
                </AuthorityChecker>
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentBrowsePage)

export const ReApplyButton = styled(StyledSecondaryButton)`
  display: flex;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 15px;
`

const ItemDiv = styled.div`
  background-color: ${Colors.Surface};
  ${currentMedia.lessThan('sp')`
    margin: 0px -15px;
  `}
  padding: 20px 15px 20px 15px;
`

const CardContainerStyle = styled.span`
  color: ${Colors.AccentDark};
  font-size: ${FontSize.Small};
`
